import { Component } from '@theme/component';
import { ThemeEvents, CartQuantityRefreshedEvent } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // CARLYLE — C (perf-fix regression): the product-form-component add-to-cart fast path now
    // dispatches CartAddEvent with resource: undefined by design (see product-form.js — avoids a
    // ~750-860ms blocking /cart.js refetch before the drawer can open), so #onCartUpdate's early
    // return below skips this announcement on every add. CartQuantityRefreshedEvent is that same
    // fetch running fire-and-forget instead; mirrors cart-drawer.js's #handleQuantityRefreshed.
    document.addEventListener(CartQuantityRefreshedEvent.eventName, this.#onQuantityRefreshed);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(CartQuantityRefreshedEvent.eventName, this.#onQuantityRefreshed);
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.#announce(cartCount);
  };

  /**
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onQuantityRefreshed = (event) => {
    const cartCount = event.detail?.resource?.item_count;
    if (cartCount === undefined) return; // failed fetch (see product-form.js) — nothing to announce

    this.#announce(cartCount);
  };

  /** @param {number} cartCount */
  #announce(cartCount) {
    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
